import { v4 as uuid } from 'uuid'
import {
    Broker_OrderType,
    Broker_Variety,
    Order,
    OrderType,
    Product,
    TransactionType,
    Validity,
} from '../proto/generated/order'
import { SlTargetValueType, TemplateOrderExecutionParameters } from '../proto/generated/quick-trade'
import { Security } from '../proto/generated/security'
import { computeSlTargetDisplayPriceFromBase } from './quickTradeQuote'
import { getRoundedPrice } from './orders'

export type AppendTemplateSlTargetLegsParams = {
    templateParams: TemplateOrderExecutionParameters
    mainSecurity: Security
    /** Display-unit entry price for single buy/sell templates */
    entryPriceDisplay?: number
    quantity: number
    product: Product
    batchId: string
    basketId?: string
    strategyGroup?: string
    slClientOrderId?: string
    targetClientOrderId?: string
}

function slTargetValueTypeToUi(
    valueType: SlTargetValueType | undefined
): 'points' | 'percentage' {
    return valueType === SlTargetValueType.PERCENTAGE ? 'percentage' : 'points'
}

export function appendTemplateSlTargetLegs(
    orders: Order[],
    params: AppendTemplateSlTargetLegsParams
): void {
    const p = params.templateParams
    const pm = params.mainSecurity.priceMultiplier ?? 100
    const tick = params.mainSecurity.tickSize ?? 5
    const isSingleTemplate = p.transactionType != null

    if (isSingleTemplate) {
        const tx = p.transactionType!
        const exitTx = tx === TransactionType.BUY ? TransactionType.SELL : TransactionType.BUY
        const entryDisplay = params.entryPriceDisplay

        if (p.addStopLoss && p.stopLoss && entryDisplay != null && Number.isFinite(entryDisplay)) {
            const slDisplay = computeSlTargetDisplayPriceFromBase({
                basePriceDisplay: entryDisplay,
                transactionType: tx,
                kind: 'stopLoss',
                value: p.stopLoss,
                valueType: slTargetValueTypeToUi(p.stopLossType),
            })
            if (slDisplay != null) {
                orders.push({
                    security: {
                        code: params.mainSecurity.code ?? '',
                        exchange: params.mainSecurity.exchange ?? 0,
                    },
                    transactionType: exitTx,
                    orderType: OrderType.STOP_LOSS,
                    brokerOrderType: Broker_OrderType.STOPLOSS_MARKET_ORDER,
                    quantity: params.quantity,
                    isBasket: false,
                    batchId: params.batchId,
                    orderDate: Date.now(),
                    product: params.product,
                    price: 0,
                    triggerPrice: getRoundedPrice(slDisplay * pm, tick),
                    brokerVariety: Broker_Variety.REGULAR,
                    validity: Validity.DAY,
                    clientOrderId: params.slClientOrderId ?? uuid(),
                    orderExecutionParams: {},
                    immediateExecutePaperOrder: false,
                    marketProtection: -1,
                    ...(params.strategyGroup ? { strategyGroup: params.strategyGroup } : {}),
                })
            }
        }

        if (p.addTarget && p.target && entryDisplay != null && Number.isFinite(entryDisplay)) {
            const targetDisplay = computeSlTargetDisplayPriceFromBase({
                basePriceDisplay: entryDisplay,
                transactionType: tx,
                kind: 'target',
                value: p.target,
                valueType: slTargetValueTypeToUi(p.targetType),
            })
            if (targetDisplay != null) {
                orders.push({
                    security: {
                        code: params.mainSecurity.code ?? '',
                        exchange: params.mainSecurity.exchange ?? 0,
                    },
                    transactionType: exitTx,
                    orderType: OrderType.TARGET,
                    brokerOrderType: Broker_OrderType.LIMIT_ORDER,
                    quantity: params.quantity,
                    isBasket: false,
                    batchId: params.batchId,
                    orderDate: Date.now(),
                    product: params.product,
                    price: getRoundedPrice(targetDisplay * pm, tick),
                    triggerPrice: 0,
                    brokerVariety: Broker_Variety.REGULAR,
                    validity: Validity.DAY,
                    clientOrderId: params.targetClientOrderId ?? uuid(),
                    orderExecutionParams: {},
                    immediateExecutePaperOrder: false,
                    ...(params.strategyGroup ? { strategyGroup: params.strategyGroup } : {}),
                })
            }
        }
        return
    }

    if (!params.basketId) return

    if (p.addStopLoss && p.stopLoss) {
        orders.push({
            security: {
                code: params.mainSecurity.code ?? '',
                exchange: params.mainSecurity.exchange ?? 0,
            },
            transactionType: TransactionType.BUY,
            orderType: OrderType.STOP_LOSS,
            brokerOrderType: Broker_OrderType.STOPLOSS_MARKET_ORDER,
            quantity: 1,
            isBasket: true,
            basketId: params.basketId,
            batchId: params.batchId,
            orderDate: Date.now(),
            product: Product.CNC,
            price: 0,
            triggerPrice: Math.round(p.stopLoss * pm),
            brokerVariety: Broker_Variety.REGULAR,
            validity: Validity.DAY,
            clientOrderId: params.slClientOrderId ?? uuid(),
            orderExecutionParams: {},
            immediateExecutePaperOrder: false,
            marketProtection: -1,
            ...(params.strategyGroup ? { strategyGroup: params.strategyGroup } : {}),
        })
    }

    if (p.addTarget && p.target) {
        orders.push({
            security: {
                code: params.mainSecurity.code ?? '',
                exchange: params.mainSecurity.exchange ?? 0,
            },
            transactionType: TransactionType.BUY,
            orderType: OrderType.TARGET,
            brokerOrderType: Broker_OrderType.LIMIT_ORDER,
            quantity: 1,
            isBasket: true,
            basketId: params.basketId,
            batchId: params.batchId,
            orderDate: Date.now(),
            product: Product.CNC,
            price: Math.round(p.target * pm),
            triggerPrice: 0,
            brokerVariety: Broker_Variety.REGULAR,
            validity: Validity.DAY,
            clientOrderId: params.targetClientOrderId ?? uuid(),
            orderExecutionParams: {},
            immediateExecutePaperOrder: false,
            ...(params.strategyGroup ? { strategyGroup: params.strategyGroup } : {}),
        })
    }
}
